import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { loadable } from 'jotai/utils';
import type { AggregatedFreeCashFlow as AggregatedFreeCashFlowType } from 'types/evaluation';
import type { AggregatedFreeCashFlowResultResponse } from 'types/api-responses';
import { sessionResourceAtom } from 'atoms/root';
import type { CalculationDetail } from './types';
import { runCaseScenarioId } from 'lib/evaluation-case/run-scenario';
import type {
  AriesMdbStepEvent,
  AriesMdbExportConfig,
  AriesRestoredRun,
} from 'components/aries-mdb-export/types';
import {
  // Lazy GET atoms
  workspacesAtom,
  eval2RunsAtom,
  // DPS list (mutation atom with loadable)
  fetchDpsListLoadableAtom,
  fetchDrsListLoadableAtom,
  selectedDrsIdAtom,
  fetchDifferentialScenarioListLoadableAtom,
  selectedDifferentialScenarioIdAtom,
  fetchTaxScenarioListLoadableAtom,
  selectedTaxScenarioIdAtom,
  fetchExpenseScenarioListLoadableAtom,
  selectedExpenseScenarioIdAtom,
  // Evaluation Case atoms
  selectedEvalModeAtom,
  selectedDcaScenarioIdAtom,
  selectedEvaluationCaseIdAtom,
  fetchEvaluationCaseListLoadableAtom,
  // Selection atoms
  selectedWorkspaceIdAtom,
  selectedCalculationIdAtom,
  selectedEval2IdAtom,
  selectedScenarioIdAtom,
  selectedPriceDeckIdAtom,
  // Working copy atoms
  workingEffectiveDateAtom,
  workingIntervalAtom,
  workingMeasurementTypeAtom,
  workingSlotVolumeSourceAtom,
  workingSlotVolumeConfigurationsAtom,
  workingSlotAfeSourceAtom,
  workingSlotAfeConfigurationsAtom,
  workingSlotCostDateConfigurationsAtom,
  workingSlotDateConfigurationsAtom,
  // Derived atoms
  isZeroSumAtom,
  isDirtyAtom,
  isValidConfigAtom,
  currentConfigAtom,
  activeWorkspaceIdAtom,
  calculationResultReadyCalcIdAtom,
  // Write atoms
  hydrateWorkingCopyAtom,
  // Cache + fetch atoms
  calculationsCacheAtom,
  fetchCalculationsBaseAtom,
  // Mutation atoms
  fetchCalculationDetailBaseAtom,
  fetchCalculationResultBaseAtom,
  saveEvaluationBaseAtom,
  createWorkspaceBaseAtom,
  renameWorkspaceBaseAtom,
  // INS-8866 Executive Summary
  fetchPreCalcExecutiveSummaryBaseAtom,
  mergeExecutiveSummaryAtom,
  executiveSummaryAtom,
  // Aries MDB per-calc restore
  fetchAriesLatestForCalculationBaseAtom,
} from './atoms';
import { useEvaluationCaseDetail } from '../eval3-new/hooks';
import { useCalculate, useUnsavedChanges, useExportAries } from './hooks';
import { useDpsScheduling } from './use-dps-scheduling';
import { useAriesMdbProgress } from './use-aries-mdb-progress';
import { useAriesMdbExport } from './use-aries-mdb-export';
import { useNotifications } from './use-notifications';
import { useScheduledDates } from './use-scheduled-dates';
import { useWorkspaceActions } from './use-workspace-actions';
import {
  extractWorkingCopyFromDetail,
  convertToAggregatedFreeCashFlow,
  backfillAfeConfigurations,
  buildPendingOperations,
  buildPendingSettings,
  workingDcaScenarioId,
  restoredScheduledSlotsBlobPath,
  isCreatedByCurrentUser,
} from './util';
import { downloadFromUrl } from 'utils/download-blob';
import { packageAtom, useScenariosAtom } from '../atoms';
import { EvalMainContent } from './eval-main-content';
import { EvalLeftPanel } from './left-panel/eval-left-panel';
import type { ViewMode } from './left-panel/view-filter-dropdown';
import { UnsavedChangesModal } from 'components/unsaved-changes-modal';
import { CreateWorkspaceModal } from './modals/create-workspace-modal';
import { SaveEvaluationModal } from './modals/save-evaluation-modal';
import { RenameWorkspaceModal } from './modals/rename-workspace-modal';
import { AriesMdbExportModal } from 'components/aries-mdb-export';
import { AgentSurface } from 'components/agent-chat';
import {
  applyStagedSettings,
  readStagedChange,
  stagedChangeRefusal,
  unappliedStageWarning,
} from 'components/agent-chat/staged-changes';
import type { ToolStatus } from 'components/agent-chat/types';
import { EVAL_AGENT } from 'components/agent-chat/agents/eval-agent';
import style from './eval-nav-layout.module.scss';

interface EvalNavLayoutProps {
  packageId: number;
  /**
   * Optional calculation ID supplied by a deep link
   * (`/packages/:id/eval3/:calcId`). When set, the workspace skips the
   * default auto-pick-first-workspace flow, fetches this calc's detail to
   * resolve its parent workspace, and hydrates the working copy from it.
   */
  initialCalcId?: number;
  /**
   * Called whenever the user selects a different calculation tile so the
   * caller can keep the URL in sync with the in-memory selection.
   */
  onCalcSelect?: (calcId: number) => void;
}

/**
 * Merge one set of fields into every named slot's entry in a working map.
 *
 * Every working map here has the same shape — slot id to a partial config — so the four
 * staged-change branches in `handleAgentToolResult` differed only in WHICH map and WHICH
 * fields, never in how the merge ran. They each spelled this reducer out inline anyway.
 *
 * @param {Function} setWorking - Setter for the working map being merged into.
 * @param {string[]} slotIds - The slots the staged change covers.
 * @param {object} fields - The fields to merge into each of those slots.
 * @return {void}
 */
function mergeBySlot<V>(
  setWorking: (updater: (prev: Record<string, V>) => Record<string, V>) => void,
  slotIds: string[],
  fields: Partial<V>
): void {
  setWorking((prev) => {
    const next = { ...prev };
    slotIds.forEach((slotId) => {
      next[slotId] = { ...next[slotId], ...fields } as V;
    });
    return next;
  });
}

/**
 * State coordinator for the Eval 3.0 workspace. Reads all atoms, manages
 * lifecycle effects (page load, tile click, calculate, save), and assembles
 * props for the presentational EvalMainContent component.
 *
 * @param {EvalNavLayoutProps} props - Component props.
 * @return {JSX.Element} The full workspace layout.
 */
export const EvalNavLayout = ({
  packageId,
  initialCalcId,
  onCalcSelect,
}: EvalNavLayoutProps): JSX.Element => {
  // ---------------------------------------------------------------------------
  // Lazy atoms — trigger setters + loadable readers
  // ---------------------------------------------------------------------------
  const triggerWorkspaces = useSetAtom(workspacesAtom(packageId));
  const triggerEval2Runs = useSetAtom(eval2RunsAtom(packageId));
  const [dpsLoadable, triggerDpsList] = useAtom(fetchDpsListLoadableAtom);
  const [drsLoadable, triggerDrsList] = useAtom(fetchDrsListLoadableAtom);
  const [dsnLoadable, triggerDsnList] = useAtom(
    fetchDifferentialScenarioListLoadableAtom
  );
  const [taxScenarioLoadable, triggerTaxScenarioList] = useAtom(
    fetchTaxScenarioListLoadableAtom
  );
  const [expenseScenarioLoadable, triggerExpenseScenarioList] = useAtom(
    fetchExpenseScenarioListLoadableAtom
  );

  const wsLoadableAtom = useMemo(
    () => loadable(workspacesAtom(packageId)),
    [packageId]
  );
  const eval2LoadableAtom = useMemo(
    () => loadable(eval2RunsAtom(packageId)),
    [packageId]
  );

  const workspacesLoadable = useAtomValue(wsLoadableAtom);
  const eval2Loadable = useAtomValue(eval2LoadableAtom);

  // ---------------------------------------------------------------------------
  // Selection atoms
  // ---------------------------------------------------------------------------
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useAtom(
    selectedWorkspaceIdAtom(packageId)
  );
  const [selectedCalcId, setSelectedCalcId] = useAtom(
    selectedCalculationIdAtom(packageId)
  );
  // Legacy: kept readable so the eval2 link header / comparison panels
  // still pick up the hydrated id for old calcs. INS-8827 retires write
  // access from this component.
  const selectedEval2Id = useAtomValue(selectedEval2IdAtom(packageId));
  const [selectedScenarioId, setSelectedScenarioId] = useAtom(
    selectedScenarioIdAtom(packageId)
  );
  const selectedPriceDeckId = useAtomValue(selectedPriceDeckIdAtom(packageId));
  const hydrateWC = useSetAtom(hydrateWorkingCopyAtom(packageId));
  const setCalcResultReadyId = useSetAtom(
    calculationResultReadyCalcIdAtom(packageId)
  );
  const calcResultReady = useAtomValue(
    calculationResultReadyCalcIdAtom(packageId)
  );

  // ---------------------------------------------------------------------------
  // Calculations cache + fetch + active workspace
  // ---------------------------------------------------------------------------
  const calculationsCache = useAtomValue(calculationsCacheAtom(packageId));
  const fetchCalculations = useSetAtom(fetchCalculationsBaseAtom);
  const activeWorkspaceId = useAtomValue(activeWorkspaceIdAtom(packageId));

  // ---------------------------------------------------------------------------
  // Working copy atoms
  // ---------------------------------------------------------------------------
  const [workingEffectiveDate, setWorkingEffectiveDate] = useAtom(
    workingEffectiveDateAtom(packageId)
  );
  const [workingInterval, setWorkingInterval] = useAtom(
    workingIntervalAtom(packageId)
  );
  const [workingMeasurementType, setWorkingMeasurementType] = useAtom(
    workingMeasurementTypeAtom(packageId)
  );
  const [workingSlotVolumeSource, setWorkingSlotVolumeSource] = useAtom(
    workingSlotVolumeSourceAtom(packageId)
  );
  const [workingSlotVolumeConfigurations, setWorkingSlotVolumeConfigurations] =
    useAtom(workingSlotVolumeConfigurationsAtom(packageId));

  const [workingSlotAfeConfigurations, setWorkingSlotAfeConfigurations] =
    useAtom(workingSlotAfeConfigurationsAtom(packageId));

  const [workingSlotAfeSource, setWorkingSlotAfeSource] = useAtom(
    workingSlotAfeSourceAtom(packageId)
  );

  const [
    workingSlotCostDateConfigurations,
    setWorkingSlotCostDateConfigurations,
  ] = useAtom(workingSlotCostDateConfigurationsAtom(packageId));

  const [workingSlotDateConfigurations, setWorkingSlotDateConfigurations] =
    useAtom(workingSlotDateConfigurationsAtom(packageId));

  // DPS scheduled dates fetched from the blob via SAS URL
  const scheduledDates = useScheduledDates(packageId);

  // ---------------------------------------------------------------------------
  // Derived atoms
  // ---------------------------------------------------------------------------
  const isZeroSum = useAtomValue(isZeroSumAtom(packageId));
  const isDirty = useAtomValue(isDirtyAtom(packageId));
  const isValidConfig = useAtomValue(isValidConfigAtom(packageId));
  const currentConfig = useAtomValue(currentConfigAtom(packageId));
  const session = useAtomValue(sessionResourceAtom);

  // ---------------------------------------------------------------------------
  // Mutation atom setters
  // ---------------------------------------------------------------------------
  const fetchCalculationDetail = useSetAtom(fetchCalculationDetailBaseAtom);
  const fetchCalculationResult = useSetAtom(fetchCalculationResultBaseAtom);
  const saveEvaluation = useSetAtom(saveEvaluationBaseAtom);
  const createWorkspace = useSetAtom(createWorkspaceBaseAtom);
  const renameWorkspace = useSetAtom(renameWorkspaceBaseAtom);

  // INS-8866 Executive Summary — pre-calc fetch + post-calc merge.
  const fetchPreCalcExecutiveSummary = useSetAtom(
    fetchPreCalcExecutiveSummaryBaseAtom
  );
  const mergeExecutiveSummary = useSetAtom(
    mergeExecutiveSummaryAtom(packageId)
  );
  // Pricing keys (Target/Max NPV) are workspace-specific — populated only by
  // a successful eval_result fetch. Tract/slot/operator/formation keys are
  // package-scoped (pre-calc API takes no workspace param) and stay valid
  // across workspace switches, so we only blank the pricing keys here.
  const setExecutiveSummary = useSetAtom(executiveSummaryAtom(packageId));
  const resetExecutiveSummaryPricing = useCallback(() => {
    setExecutiveSummary((prev) => ({
      ...prev,
      targetNpvExcluded: undefined,
      targetNpvIncluded: undefined,
      maxNpvExcluded: undefined,
      maxNpvIncluded: undefined,
    }));
  }, [setExecutiveSummary]);

  // ---------------------------------------------------------------------------
  // Notifications
  // ---------------------------------------------------------------------------
  const {
    notifications,
    pushNotification,
    dismissNotification,
    clearNotifications,
    clearNotificationsBySource,
  } = useNotifications(packageId);

  // ---------------------------------------------------------------------------
  // Hooks
  // ---------------------------------------------------------------------------
  const refetchCalcsForWorkspace = useCallback(
    (wsId: number) => {
      fetchCalculations(packageId, wsId);
    },
    [fetchCalculations, packageId]
  );

  const {
    isCalculating,
    error: calcError,
    calculationWarnings,
    triggerCalculation,
    resumePendingCalculation,
    cancelCalculation,
  } = useCalculate(packageId, refetchCalcsForWorkspace);

  const unsavedChanges = useUnsavedChanges(packageId);

  // The selected case's detail, through the eval page's one query for it (refetched on focus, so a
  // case edited in Case Management shows here on return): a case run's DCA is the case's, so the
  // DPS schedule and the slot tables read it from here.
  const { data: evaluationCaseDetail = null } = useEvaluationCaseDetail(
    currentConfig.evaluationCaseId ?? null
  );

  // ---------------------------------------------------------------------------
  // DPS scheduling hook
  // ---------------------------------------------------------------------------
  const {
    selectedDpsId,
    dpsScheduleResult,
    setDpsScheduleResult,
    dpsScheduleStatus,
    setDpsScheduleStatus,
    handleDpsChange,
    handleScenarioChange,
    snapshotProgress,
  } = useDpsScheduling({
    packageId,
    selectedScenarioId,
    dcaScenarioId: workingDcaScenarioId(currentConfig, evaluationCaseDetail),
    dpsId: currentConfig.dpsId,
    pushNotification,
    clearNotificationsBySource,
    setWorkingSlotDateConfigurations,
  });

  // DRS selection — no scheduling needed, resolved synchronously at eval start
  const [selectedDrsId, setSelectedDrsId] = useAtom(
    selectedDrsIdAtom(packageId)
  );

  // Differential Scenario selection
  const [selectedDifferentialScenarioId, setSelectedDifferentialScenarioId] =
    useAtom(selectedDifferentialScenarioIdAtom(packageId));

  // Tax Scenario selection — resolved synchronously at eval start
  const [selectedTaxScenarioId, setSelectedTaxScenarioId] = useAtom(
    selectedTaxScenarioIdAtom(packageId)
  );

  // Expense Scenario selection — resolved synchronously at eval start
  const [selectedExpenseScenarioId, setSelectedExpenseScenarioId] = useAtom(
    selectedExpenseScenarioIdAtom(packageId)
  );

  // Evaluation Case selection (INS-9947)
  const [evalMode, setEvalMode] = useAtom(selectedEvalModeAtom(packageId));
  const [selectedEvaluationCaseId, setSelectedEvaluationCaseId] = useAtom(
    selectedEvaluationCaseIdAtom(packageId)
  );
  // Written only by the agent's staged settings change; the drawer owns the
  // picker. `currentConfigAtom` reads this into the Calculate payload.
  // Read as well as written: the agent needs to know which DCA scenario the page is
  // holding, not just be able to set it. Write-only, the pending-settings diff could not
  // see a staged DCA change at all.
  const [selectedDcaScenarioId, setSelectedDcaScenarioId] = useAtom(
    selectedDcaScenarioIdAtom(packageId)
  );
  const [caseListState, fetchCaseList] = useAtom(
    fetchEvaluationCaseListLoadableAtom
  );

  // Fetch case list once on mount — do NOT re-fetch when selection changes,
  // otherwise the list goes empty during loading and ComboBox deselects.
  useEffect(() => {
    fetchCaseList(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const evaluationCaseList = useMemo(
    () => (caseListState.state === 'hasData' ? (caseListState.data ?? []) : []),
    [caseListState]
  );

  // INS-8827: global Scenario index — feeds both the in-page Scenario
  // picker (GlobalSettings) and the create-workspace modal's Scenario
  // dropdown. `useScenariosAtom` returns a resource atom whose value is
  // already shaped as `{ loading, data }` (see lib/atoms.createResourceAtom).
  const scenariosBaseAtom = useScenariosAtom();
  const scenariosResource = useAtomValue(scenariosBaseAtom);
  const scenarios =
    scenariosResource &&
    !scenariosResource.loading &&
    scenariosResource.data &&
    !('error' in scenariosResource.data)
      ? scenariosResource.data
      : undefined;

  // ---------------------------------------------------------------------------
  // Local state
  // ---------------------------------------------------------------------------
  const [leftPanelCollapsed, setLeftPanelCollapsed] = useState(false);
  const [currentDetail, setCurrentDetail] = useState<CalculationDetail | null>(
    null
  );
  const [resultData, setResultData] =
    useState<AggregatedFreeCashFlowType | null>(null);
  const [resultError, setResultError] = useState('');
  const [isDetailLoading, setIsDetailLoading] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('all');
  const [loadingCalcsWsId, setLoadingCalcsWsId] = useState<number | null>(null);
  // Tracks whether a deep-link calc is still being resolved to its workspace.
  // While true, the auto-pick-first-workspace effect is suppressed so the
  // resolver doesn't race with the default landing flow.
  const [isDeepLinkPending, setIsDeepLinkPending] = useState(
    initialCalcId !== undefined
  );
  // Tag export-aries failures with their own source so successive retries
  // dedupe rather than piling errors in the bar.
  const handleExportAriesError = useCallback(
    (msg: string) => pushNotification(msg, 'export-aries'),
    [pushNotification]
  );
  const { exportAries: handleExportAries, isExportAriesLoading } =
    useExportAries(selectedCalcId, handleExportAriesError);

  // ---------------------------------------------------------------------------
  // Aries MDB export — config modal + live progress + per-calc restore.
  //
  // `useAriesMdbProgress` is mounted HERE (parent) so per-step progress survives
  // the checklist popover opening and closing (no lost progress on reopen). The
  // popover + entry button + download are threaded down through EvalMainContent,
  // distinct from the DPS snapshot popover.
  //
  // Two sources feed the progress hook, never at once:
  //   • Fresh kickoff — the entry button opens the config modal; on Export the
  //     kickoff `201`'s notifications become `events` + `channel` (all rows start
  //     Pending, live subscription bound).
  //   • Restore — the selected calc's latest run is re-derived from the server
  //     (`latest_for_calculation`) on tile-switch / reload and handed in as
  //     `restore`. The server is the source of truth, so switching calcs shows
  //     the newly-selected calc's own run (or nothing) — no cross-calc bleed.
  // ---------------------------------------------------------------------------
  const [ariesMdbChannel, setAriesMdbChannel] = useState<string | null>(null);
  const [ariesMdbEvents, setAriesMdbEvents] = useState<
    AriesMdbStepEvent[] | null
  >(null);
  // The selected calc's restored run (null for a fresh run or a calc with none).
  const [ariesMdbRestore, setAriesMdbRestore] =
    useState<AriesRestoredRun | null>(null);
  const [isAriesMdbModalOpen, setIsAriesMdbModalOpen] = useState(false);
  // Flips the modal from the Config form to the live run views once a kickoff
  // succeeds. Reset to false on every (re)open so a fresh open always shows
  // Config — even if a prior run's `overallStatus` is still terminal.
  const [ariesMdbRunStarted, setAriesMdbRunStarted] = useState(false);
  const fetchAriesLatest = useSetAtom(fetchAriesLatestForCalculationBaseAtom);
  const ariesMdbProgress = useAriesMdbProgress({
    channel: ariesMdbChannel,
    events: ariesMdbEvents,
    restore: ariesMdbRestore,
  });
  // The restored run's config prefills the modal on a re-run; null falls back to
  // the server-derived `/new` defaults.
  const ariesMdbExport = useAriesMdbExport(
    selectedCalcId,
    ariesMdbRestore?.config ?? null
  );
  const {
    loadDefaults: loadAriesMdbDefaults,
    submit: submitAriesMdbExport,
    clearError: clearAriesMdbError,
  } = ariesMdbExport;

  const handleOpenAriesMdbModal = useCallback(() => {
    if (!selectedCalcId) return;
    // If a run is still in flight, reopen straight into its live progress view
    // instead of a fresh Config — "Run in background" keeps `runStarted` and the
    // channel/events, so we only need to re-show the modal. This also stops a
    // second run being kicked off while one is already going. Only fall back to a
    // fresh Config (reset + reload defaults) when nothing is running or the last
    // run already finished/failed.
    const runInFlight =
      ariesMdbChannel !== null &&
      ariesMdbProgress.overallStatus !== 'processed' &&
      ariesMdbProgress.overallStatus !== 'failed';
    if (runInFlight) {
      setIsAriesMdbModalOpen(true);
      return;
    }
    setAriesMdbRunStarted(false);
    setIsAriesMdbModalOpen(true);
    // `loadDefaults` also clears any prior error, so the modal opens clean.
    loadAriesMdbDefaults();
  }, [
    selectedCalcId,
    ariesMdbChannel,
    ariesMdbProgress.overallStatus,
    loadAriesMdbDefaults,
  ]);

  const handleCloseAriesMdbModal = useCallback(() => {
    setIsAriesMdbModalOpen(false);
    clearAriesMdbError();
  }, [clearAriesMdbError]);

  // Progress-view "Run in background": hide the modal but leave the run going —
  // the background WorkflowStatusPopover keeps tracking it to completion. This
  // is the pre-INS-10151 close-on-kickoff behavior, now user-initiated.
  const handleRunInBackgroundAriesMdb = useCallback(() => {
    setIsAriesMdbModalOpen(false);
  }, []);

  const handleSubmitAriesMdb = useCallback(
    async (config: AriesMdbExportConfig) => {
      const response = await submitAriesMdbExport(config);
      if (!response) return; // Error stays inline in the modal; keep it open.
      // Hand the kickoff notifications to the live-progress hook (a fresh run
      // carries a new channel + events, so `useAriesMdbProgress` re-initializes
      // the checklist and re-subscribes on its own). Clear any restored run so
      // the fresh live events aren't shadowed by the seed path. Keep the modal
      // OPEN and flip it to the live progress view.
      setAriesMdbRestore(null);
      setAriesMdbEvents(response.resources.notifications.events);
      setAriesMdbChannel(response.resources.notifications.channel);
      setAriesMdbRunStarted(true);
    },
    [submitAriesMdbExport]
  );

  // Per-calc restore: re-derive the selected calc's latest Aries run from the
  // server (source of truth) whenever the selected calc changes — covering both
  // tile-switch and page reload (the auto-pick effect routes reload through
  // `selectedCalcId`). This replaces the temporary null-out-on-switch stopgap:
  // an in-progress run resumes (seed + re-subscribe), a terminal run seeds its
  // completion/failed state (+ re-offers the download), and a calc with no run
  // clears any prior calc's state so nothing bleeds across calcs.
  useEffect(() => {
    // Clear the prior calc's run state SYNCHRONOUSLY, before the async fetch —
    // otherwise, during the network round-trip, the previous calc's popover,
    // download, and (worse) still-subscribed live Pusher channel keep rendering
    // and applying under the newly-selected calc. This synchronous teardown is
    // the anti-bleed guarantee; the `cancelled` guard below only stops a stale
    // out-of-order resolve from seeding once the fetch returns.
    setIsAriesMdbModalOpen(false);
    setAriesMdbRunStarted(false);
    setAriesMdbRestore(null);
    setAriesMdbEvents(null);
    setAriesMdbChannel(null);

    if (selectedCalcId === null) return;

    let cancelled = false;
    const calcId = selectedCalcId;
    (async () => {
      const restored = await fetchAriesLatest(calcId);
      // A newer calc selection may have superseded this fetch — only the latest
      // selected calc may seed state.
      if (cancelled) return;

      setAriesMdbRestore(restored);
      // Only keep a live subscription for a run that is still in progress; a
      // terminal run is seeded directly from the payload, and no run stays clear.
      setAriesMdbChannel(restored?.should_subscribe ? restored.channel : null);
      // Mark an in-progress restored run as started so reopening the entry point
      // lands in the live progress view (and the duplicate-kickoff guard stays
      // armed). A terminal run keeps runStarted=false so reopen falls to a fresh
      // Config for a re-run — which the restored config then prefills.
      setAriesMdbRunStarted(!!restored?.should_subscribe);
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedCalcId, fetchAriesLatest]);

  // Auto-download on completion: the moment a LIVE run finishes with a bundle,
  // fire the same same-tab download the completion view's button triggers —
  // whether the modal is open or the run was backgrounded ("Run in background"
  // only hides the modal; this hook-owning parent keeps tracking the run).
  //
  // The armed ref scopes the auto-fire to runs watched in-flight this session:
  // it arms only while a live run is building (fresh kickoff, or a restored
  // still-in-progress run), fires exactly once on completion, and disarms
  // whenever no run is live — so a restored already-terminal run (tile switch /
  // page reload re-offering an old bundle) never re-downloads on its own.
  const ariesMdbAutoDownloadArmed = useRef(false);
  const ariesMdbBundleUrl = ariesMdbProgress.bundle?.downloadUrl;
  useEffect(() => {
    if (!ariesMdbRunStarted) {
      ariesMdbAutoDownloadArmed.current = false;
      return;
    }
    if (ariesMdbProgress.isBuilding) {
      ariesMdbAutoDownloadArmed.current = true;
      return;
    }
    // Overall `processed` and the bundle step's `output` arrive as separate
    // Pusher events in no guaranteed order; this effect re-runs on both, so it
    // fires once both are in hand.
    if (
      ariesMdbAutoDownloadArmed.current &&
      ariesMdbProgress.isComplete &&
      ariesMdbBundleUrl
    ) {
      ariesMdbAutoDownloadArmed.current = false;
      // Dismiss the modal (still open on the live progress view unless the run
      // was backgrounded) BEFORE firing the download — the export is done and
      // the file is saving, so there's no completion dialog to close by hand.
      setIsAriesMdbModalOpen(false);
      downloadFromUrl(ariesMdbBundleUrl);
    }
  }, [
    ariesMdbRunStarted,
    ariesMdbProgress.isBuilding,
    ariesMdbProgress.isComplete,
    ariesMdbBundleUrl,
  ]);

  // ---------------------------------------------------------------------------
  // Extracted data from loadables
  // ---------------------------------------------------------------------------
  const eval2Runs =
    eval2Loadable.state === 'hasData' && eval2Loadable.data
      ? eval2Loadable.data
      : undefined;

  const dpsList =
    dpsLoadable.state === 'hasData' && dpsLoadable.data
      ? dpsLoadable.data
      : undefined;

  // ---------------------------------------------------------------------------
  // Workspace actions hook
  // ---------------------------------------------------------------------------
  const {
    handleCreateWorkspace,
    handleCalculate,
    handleSave,
    handleWorkspaceExpand,
    handleRename,
    isCreateLoading,
    isSaveLoading,
    isRenameLoading,
    isCreateModalOpen,
    setIsCreateModalOpen,
    isSaveModalOpen,
    setIsSaveModalOpen,
    isRenameModalOpen,
    setIsRenameModalOpen,
  } = useWorkspaceActions({
    packageId,
    selectedWorkspaceId,
    activeWorkspaceId,
    selectedScenarioId,
    selectedCalcId,
    selectedPriceDeckId,
    currentConfig,
    currentDetail,
    isZeroSum,
    dpsScheduleResult,
    calculationsCache,
    setSelectedWorkspaceId,
    setSelectedScenarioId,
    setSelectedEvalMode: setEvalMode,
    setSelectedEvaluationCaseId,
    setSelectedCalcId,
    setCurrentDetail,
    setResultData,
    setResultError,
    resetExecutiveSummaryPricing,
    pushNotification,
    clearNotifications,
    clearNotificationsBySource,
    handleDpsChange,
    triggerWorkspaces,
    triggerCalculation,
    saveEvaluation,
    createWorkspace,
    renameWorkspace,
    fetchCalculations,
    setLoadingCalcsWsId,
  });

  const pkgLoadable = useAtomValue(packageAtom);
  const pkgData =
    pkgLoadable.state === 'hasData' &&
    pkgLoadable.data &&
    !('error' in pkgLoadable.data)
      ? pkgLoadable.data
      : null;
  const partiallyCoveredWellsHref =
    pkgData?.resources.partiallyCoveredWells.href ?? '';
  const partiallyCoveredWellsCount =
    pkgData?.partiallyCoveredWellsCount ?? null;

  // Derive eval date and expiration from the eval3 calculation detail
  // instead of calling the eval2 package_overview endpoint (which is slow
  // and not relevant to eval3).
  const evalDate = currentDetail?.createdAt ?? null;
  const evalExpiration = currentDetail?.createdAt
    ? new Date(
        new Date(currentDetail.createdAt).getTime() + 14 * 24 * 60 * 60 * 1000
      )
        .toISOString()
        .split('T')[0]
    : null;

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  /**
   * Handles clicking a calculation tile. Fetches detail, hydrates working
   * copy atoms, and starts or resumes the calculation if needed.
   *
   * @param {number} calcId - The calculation ID that was clicked.
   */
  const handleTileClick = useCallback(
    (calcId: number) => {
      unsavedChanges.guardNavigation(async () => {
        clearNotifications();
        cancelCalculation();
        setSelectedCalcId(calcId);
        onCalcSelect?.(calcId);
        setResultData(null);
        setResultError('');
        setIsDetailLoading(true);

        try {
          const detail = await fetchCalculationDetail(calcId);
          const wc = extractWorkingCopyFromDetail(detail);

          // Backfill AFE costs/wellName BEFORE hydration so both working copy
          // and appliedConfig receive the enriched data atomically, preventing
          // a spurious isDirty=true from the JSON.stringify comparison.
          if (Object.keys(wc.slotAfeConfigurations).length > 0) {
            wc.slotAfeConfigurations = await backfillAfeConfigurations(
              wc.slotAfeConfigurations
            );
          }

          // `setCurrentDetail` moved to HERE, after the awaited backfill, so the baseline
          // and the working maps change together.
          //
          // Set before the await, the two disagreed for as long as the backfill took: the
          // baseline was the NEW run while the maps were still the OLD one, and
          // `buildPendingOperations` diffs one against the other. A message sent in that
          // window reported edits nobody had made — and staging selection acts on those,
          // so the agent could target properties on the strength of a difference that was
          // really just a half-finished run switch.
          setCurrentDetail(detail);
          hydrateWC(wc);

          // Restore DPS schedule result so useScheduledDates can fetch the blob.
          // On return visits the original SAS URL has expired; the backend
          // generates a fresh one in the detail response.
          if (
            detail.configurations.scheduled_slots_blob_path &&
            detail.scheduledSlotsSasUrl
          ) {
            setDpsScheduleResult({
              slotsBlobPath: detail.configurations.slots_blob_path ?? '',
              scheduledSlotsBlobPath: restoredScheduledSlotsBlobPath(detail),
              scheduledSlotsSasUrl: detail.scheduledSlotsSasUrl,
              slotCount: 0,
            });
            setDpsScheduleStatus('ready');
          } else {
            setDpsScheduleResult(null);
          }

          // `calculationId` is nullable now (INS-12398) — it is null for a first run that has
          // not dispatched. A Completed run always has one; the guard is what tells TS that,
          // and it keeps the ready-signal from ever carrying a null address.
          if (detail.status === 'Completed' && detail.calculationId) {
            setCalcResultReadyId({
              id: detail.id,
              calculationId: detail.calculationId,
            });
          } else if (detail.status === 'Processing') {
            resumePendingCalculation(
              detail.id,
              detail.calculationId,
              detail.notifications,
              detail.createdAt,
              // Whose calculation list to refresh when this run finishes. Without it the run
              // resolved against workspace `null` and the nav tiles stayed on Processing until
              // the user navigated away — half the ONCALL-937 fix.
              detail.workspaceId,
              // Tells the hook WHOSE uuid the line above is: this run's once the run has
              // dispatched, the run it replaced before that (ONCALL-937).
              detail.dispatched
            );
          } else if (detail.status === 'Error') {
            setResultError(detail.error ?? 'Calculation failed');
          }
        } catch (err) {
          setResultError(
            err instanceof Error ? err.message : 'Failed to load calculation'
          );
        } finally {
          setIsDetailLoading(false);
        }
      });
    },
    [
      clearNotifications,
      unsavedChanges,
      cancelCalculation,
      setSelectedCalcId,
      onCalcSelect,
      fetchCalculationDetail,
      hydrateWC,
      setDpsScheduleResult,
      setDpsScheduleStatus,
      setCalcResultReadyId,
      resumePendingCalculation,
    ]
  );

  // ---------------------------------------------------------------------------
  // Page load effects
  // ---------------------------------------------------------------------------

  // 1. Trigger initial data fetches on mount.
  useEffect(() => {
    triggerWorkspaces();
    triggerEval2Runs();
    triggerDpsList();
    triggerDrsList();
    triggerDsnList();
    triggerTaxScenarioList();
    triggerExpenseScenarioList();
    // INS-8875 pre-calc Executive Summary. Always fetch on mount — gives the
    // panel something to show before any calc is selected (zero-sum, fresh
    // workspace, or while detail/result requests are in flight). Post-calc
    // eval_result merges over these keys; pricing keys never come from here.
    fetchPreCalcExecutiveSummary(packageId)
      .then((data) => mergeExecutiveSummary(data))
      .catch((err) => {
        console.error('[ExecutiveSummary] pre-calc fetch failed:', err);
      });
  }, [
    triggerWorkspaces,
    triggerEval2Runs,
    triggerDpsList,
    triggerDrsList,
    triggerDsnList,
    triggerTaxScenarioList,
    triggerExpenseScenarioList,
    fetchPreCalcExecutiveSummary,
    mergeExecutiveSummary,
    packageId,
  ]);

  // 2a. Deep-link resolver: when initialCalcId is supplied via the URL, fetch
  //     the calc's detail to discover its parent workspace, then pre-seed the
  //     selection atoms so the default landing flow renders the deep-linked
  //     run instead of the first workspace + first tile. Required because the
  //     URL only carries the calc id, not its workspace, and the default
  //     auto-pick effect would otherwise expand workspaces[0] in the left
  //     panel even when the deep-linked calc lives elsewhere.
  useEffect(() => {
    if (!isDeepLinkPending) return;
    if (initialCalcId === undefined) {
      setIsDeepLinkPending(false);
      return;
    }
    if (workspacesLoadable.state !== 'hasData' || !workspacesLoadable.data)
      return;

    // Capture into a local const so TypeScript preserves the
    // `number` narrowing inside the async function below.
    const calcId = initialCalcId;
    let cancelled = false;

    async function resolveDeepLink() {
      try {
        const detail = await fetchCalculationDetail(calcId);
        if (cancelled || detail.packageId !== packageId) return;
        setSelectedWorkspaceId(detail.workspaceId);
        setSelectedCalcId(calcId);
        setLoadingCalcsWsId(detail.workspaceId);
        await fetchCalculations(packageId, detail.workspaceId);
      } catch (err) {
        // default workspace + first calc, so the user still gets a working
        // page on a stale/invalid shared link.
        console.error('Eval 3.0 deep-link resolver failed:', err);
      } finally {
        if (!cancelled) {
          setLoadingCalcsWsId(null);
          setIsDeepLinkPending(false);
        }
      }
    }

    resolveDeepLink();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDeepLinkPending, initialCalcId, workspacesLoadable.state, packageId]);

  // 2b. When workspaces arrive, select the first workspace and fetch its
  //     calcs. Suppressed while a deep link is being resolved.
  useEffect(() => {
    if (isDeepLinkPending) return;
    if (workspacesLoadable.state !== 'hasData' || !workspacesLoadable.data)
      return;
    const ws = workspacesLoadable.data.workspaces;
    if (ws.length === 0 || selectedWorkspaceId !== null) return;

    const firstWsId = ws[0].id;
    setSelectedWorkspaceId(firstWsId);
    setLoadingCalcsWsId(firstWsId);
    fetchCalculations(packageId, firstWsId)
      .then(() => clearNotificationsBySource('load-calcs'))
      .catch(() =>
        pushNotification('Failed to load calculations', 'load-calcs')
      )
      .finally(() => setLoadingCalcsWsId(null));
  }, [
    isDeepLinkPending,
    workspacesLoadable,
    selectedWorkspaceId,
    setSelectedWorkspaceId,
    fetchCalculations,
    packageId,
    pushNotification,
    clearNotificationsBySource,
  ]);

  // 3. When calculations arrive for the selected workspace, auto-click the
  //    first tile — or fetch detail for an already-selected calc (zero-sum
  //    flow sets selectedCalcId via Pusher before calcs load).
  //
  //    IMPORTANT: Skip when isCalculating — handleTileClick calls
  //    cancelCalculation() which kills the active Pusher subscription.
  //    After the Pusher event fires and isCalculating becomes false, this
  //    effect re-runs and hydrates the detail normally.
  useEffect(() => {
    if (selectedWorkspaceId === null) return;
    const calcs = calculationsCache[selectedWorkspaceId];
    if (!calcs || calcs.length === 0) return;

    if (selectedCalcId === null) {
      handleTileClick(calcs[0].id);
    } else if (!currentDetail && !isCalculating) {
      handleTileClick(selectedCalcId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calculationsCache, selectedWorkspaceId, selectedCalcId, isCalculating]);

  // 4. When calcResultReadyId is set (or aggregation changes), fetch the
  //    result and convert it. The backend eagerly computes all three
  //    aggregation periods; we just re-fetch the appropriate one.
  //    Also patches currentDetail so save button and ID stay in sync
  //    after Pusher completes (without going through handleTileClick
  //    which would clear resultData).
  useEffect(() => {
    if (!calcResultReady) return;

    const { id: readyCalcId, calculationId: readyCalcUuid } = calcResultReady;

    let cancelled = false;
    (async () => {
      try {
        const raw = await fetchCalculationResult(
          readyCalcId,
          `${workingInterval}ly`
        );
        if (cancelled) return;

        // Patch currentDetail with the new calc's ID, UUID, and unsaved status
        // so the Save button appears, guards use the right ID, and the
        // cashflows-by-month table fetches with the correct calculationId.
        if (currentDetail) {
          setCurrentDetail({
            ...currentDetail,
            id: readyCalcId,
            calculationId: readyCalcUuid,
            saved: false,
            savedAt: null,
          });
        }

        const apiResponse = raw as AggregatedFreeCashFlowResultResponse;
        const firstResult = apiResponse.results?.[0];
        if (firstResult?.result && firstResult?.request) {
          setResultData(
            convertToAggregatedFreeCashFlow(
              firstResult.result,
              firstResult.request
            )
          );
          setResultError('');
        }
        if (apiResponse.executiveSummary) {
          mergeExecutiveSummary(apiResponse.executiveSummary);
        }
      } catch (err) {
        if (cancelled) return;
        setResultError(
          err instanceof Error ? err.message : 'Failed to load results'
        );
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calcResultReady, workingInterval, fetchCalculationResult]);

  // ---------------------------------------------------------------------------
  // Computed props
  // ---------------------------------------------------------------------------
  const isPageLoading =
    workspacesLoadable.state === 'loading' || isDetailLoading;
  const isSaveVisible = !!currentDetail && !currentDetail.saved;
  const isSaveDisabled = isSaveLoading;
  const targetWorkspaceId = selectedWorkspaceId ?? activeWorkspaceId;
  const isCalculateDisabled = isZeroSum
    ? !isValidConfig || isCalculating
    : !isValidConfig || !targetWorkspaceId || isCalculating;

  const calculateDisabledReason = (() => {
    if (isCalculating) return undefined;
    if (!isValidConfig) {
      if (currentConfig.scenarioId === null) {
        return 'Select a Scenario before calculating.';
      }
      if (currentConfig.evalMode === 'case') {
        if (!currentConfig.evaluationCaseId) {
          return 'Select an Evaluation Case before calculating.';
        }
        if (currentConfig.effectiveDate === '') {
          return 'Set an effective date before calculating.';
        }
        return 'Complete all required settings before calculating.';
      }
      const dpsSelected = currentConfig.dpsId !== null;
      const dpsStatus = dpsScheduleResult ? dpsScheduleStatus : null;
      if (dpsSelected && dpsStatus !== 'ready') {
        return 'Slots must be scheduled before calculating. Select a Development Pace Scenario and wait for scheduling to complete.';
      }
      if (!dpsSelected) {
        return 'Select a Development Pace Scenario before calculating.';
      }
      if (currentConfig.effectiveDate === '') {
        return 'Set an effective date before calculating.';
      }
      return 'Complete all required settings before calculating.';
    }
    if (!isZeroSum && !targetWorkspaceId) {
      return 'Select or create a workspace before calculating.';
    }
    return undefined;
  })();
  const isRenameVisible = !isZeroSum && !!activeWorkspaceId;
  const hasSlotsBlobPath = !!currentDetail?.configurations?.slots_blob_path;
  const isExportAriesDisabled =
    !currentDetail || currentDetail.status !== 'Completed' || !hasSlotsBlobPath;
  const exportAriesTooltip =
    currentDetail && currentDetail.status === 'Completed' && !hasSlotsBlobPath
      ? 'An Eval 3 run is required before exporting ARIES queries'
      : undefined;

  const allWorkspaces = useMemo(() => {
    if (workspacesLoadable.state === 'hasData' && workspacesLoadable.data) {
      return workspacesLoadable.data.workspaces;
    }
    return [];
  }, [workspacesLoadable]);

  const currentUserName = session?.profile?.name;
  const workspaces = useMemo(() => {
    if (viewMode === 'mine') {
      return allWorkspaces.filter((ws) => {
        if (isCreatedByCurrentUser(ws.createdBy, currentUserName)) return true;
        const calcs = calculationsCache[ws.id] ?? [];
        return calcs.some(
          (c) =>
            c.runByUser.name === currentUserName ||
            c.savedByUser?.name === currentUserName
        );
      });
    }
    if (viewMode === 'saved') {
      return allWorkspaces.filter((ws) => {
        const calcs = calculationsCache[ws.id] ?? [];
        return calcs.some((c) => c.saved);
      });
    }
    return allWorkspaces;
  }, [allWorkspaces, viewMode, currentUserName, calculationsCache]);

  // The workspace the chat panel belongs to. Read from `allWorkspaces` rather
  // than the view-filtered `workspaces` so switching the left panel's view mode
  // cannot pull the panel out from under an open conversation.
  const agentWorkspace = useMemo(
    () => allWorkspaces.find((ws) => ws.id === targetWorkspaceId) ?? null,
    [allWorkspaces, targetWorkspaceId]
  );

  // The open run rides along as a query param rather than in the path: Eval 3
  // switches runs client-side without a navigation, so the workspace is the only
  // thing the URL can carry. The controller drops a stale or foreign id and the
  // tools fall back to the latest completed run.
  const agentMessagesHref = useMemo(() => {
    const base = agentWorkspace?.agentMessages?.href;
    if (!base) return null;
    return selectedCalcId ? `${base}?calculation_id=${selectedCalcId}` : base;
  }, [agentWorkspace, selectedCalcId]);

  // Applies a staged change from the agent to the working copy — the same state
  // the Bulk Edit Slots modal and the Eval Settings drawer write, so it lands as
  // a pending edit and the user presses Calculate. The agent never runs a
  // calculation: that is external compute and stays a deliberate human action.
  //
  // Reading the payload lives in `readStagedChange` because this page and
  // eval3-new parse it identically and only differ in how they apply it.
  // What this page is holding that the run has not been told about. Read at send time so
  // an edit made while a reply is composing is still described by the state it went out
  // with. See `buildPendingOperations` for why these are operations, not families.
  const reportPendingState = useCallback(
    () => ({
      reported: true,
      operations: buildPendingOperations({
        // Diffed against the run that was loaded, because the working maps are hydrated
        // FROM it — without a baseline an untouched run reads as fully edited.
        baseline: currentDetail,
        slotVolumeConfigurations: workingSlotVolumeConfigurations,
        slotAfeConfigurations: workingSlotAfeConfigurations,
        slotCostDateConfigurations: workingSlotCostDateConfigurations,
        slotDateConfigurations: workingSlotDateConfigurations,
      }),
      // Run-level settings too. They are not slot overrides, so the operations log says
      // nothing about them — and after staging a change of evaluation case, every later
      // tool call read the old one and could decide against a configuration Calculate
      // would never submit.
      settings: buildPendingSettings({
        baseline: currentDetail,
        evalMode,
        scenarioId: selectedScenarioId,
        evaluationCaseId: selectedEvaluationCaseId,
        dcaScenarioId: selectedDcaScenarioId,
        costSource: workingSlotAfeSource,
        volumeSource: workingSlotVolumeSource,
        effectiveDate: workingEffectiveDate,
        priceDeckId: selectedPriceDeckId,
      }),
    }),
    [
      evalMode,
      selectedScenarioId,
      selectedEvaluationCaseId,
      selectedDcaScenarioId,
      workingSlotAfeSource,
      workingSlotVolumeSource,
      workingEffectiveDate,
      selectedPriceDeckId,
      currentDetail,
      workingSlotVolumeConfigurations,
      workingSlotAfeConfigurations,
      workingSlotCostDateConfigurations,
      workingSlotDateConfigurations,
    ]
  );

  const handleAgentToolResult = useCallback(
    (name: string | null, output: unknown, status: ToolStatus) => {
      const change = readStagedChange(name, output, status);
      if (!change) {
        // See the eval3-new handler: a tool reporting `staged: true` that yields nothing
        // to apply is the silent half-failure, and it must not return quietly.
        const unapplied = unappliedStageWarning(name, output, status);
        if (unapplied) {
          console.warn('[agent-chat] staged change not applied', {
            name,
            status,
            output,
          });
          pushNotification(unapplied, 'agent-stage-not-applied');
        }
        return;
      }

      // Only an exact match on a run that IS open may be applied — see
      // `stagedChangeRefusal`, which both eval pages share so neither can drift into
      // accepting a change staged somewhere else.
      const refusal = stagedChangeRefusal(change.calculationId, selectedCalcId);
      if (refusal) {
        pushNotification(refusal, 'agent-stale-stage');
        return;
      }

      if (change.kind === 'settings') {
        applyStagedSettings(change, {
          scenarioId: setSelectedScenarioId,
          dcaScenarioId: setSelectedDcaScenarioId,
          evaluationCaseId: setSelectedEvaluationCaseId,
        });
        return;
      }

      // Schedule dates live in their own working map here, separate from the AFE cost
      // dates, mirroring how the bulk-edit panel splits the two.
      if (change.kind === 'schedule') {
        mergeBySlot(
          setWorkingSlotDateConfigurations,
          change.slotIds,
          change.dates
        );
        return;
      }

      if (change.kind === 'volume') {
        mergeBySlot(setWorkingSlotVolumeConfigurations, change.slotIds, {
          volumeSourceId: change.volumeSourceId,
          ...(change.volumeSourceType
            ? { volumeSourceType: change.volumeSourceType }
            : {}),
          ...(change.volumeSourceHref
            ? { volumeSourceHref: change.volumeSourceHref }
            : {}),
          ...(change.forecastSource
            ? { forecastSource: change.forecastSource }
            : {}),
          ...(change.dcaScenario ? { dcaScenario: change.dcaScenario } : {}),
        });
        return;
      }

      mergeBySlot(setWorkingSlotAfeConfigurations, change.slotIds, {
        afeSourceId: change.afeSourceId,
        // Written even when null, unlike the volume branch above: afeSourceId
        // changed in this same update, so keeping the previous AFE's well name
        // would label the new source with the old name. Clearing it is the point.
        // `?? undefined` rather than passing the null through: null and undefined mean
        // the same thing to this working map — the row loses the value — and the map is
        // typed for the row, which has no null.
        afeWellName: change.afeWellName ?? undefined,
        // The link, on the same rule and for the same reason — the cell would
        // otherwise name the new AFE while navigating to the displaced AFE's well.
        afeSourceHref: change.afeSourceHref ?? undefined,
      });

      // Cost dates live in their own working map, mirroring how the bulk-edit
      // panel splits the AFE choice from the date overrides.
      if (Object.keys(change.costDates).length === 0) return;
      mergeBySlot(
        setWorkingSlotCostDateConfigurations,
        change.slotIds,
        change.costDates
      );
    },
    [
      setSelectedDcaScenarioId,
      setSelectedEvaluationCaseId,
      setSelectedScenarioId,
      setWorkingSlotAfeConfigurations,
      setWorkingSlotCostDateConfigurations,
      setWorkingSlotVolumeConfigurations,
      setWorkingSlotDateConfigurations,
      pushNotification,
      selectedCalcId,
    ]
  );

  const workspaceMeta = useMemo(
    () => ({
      workspaceCount: allWorkspaces.length,
      evaluationCount: allWorkspaces.reduce(
        (sum, ws) => sum + ws.calculationCount,
        0
      ),
    }),
    [allWorkspaces]
  );

  // The DCA scenario a completed run of the selected case ran with, and the one
  // the eval shows. A new DCA workspace opens on the second.
  const runDcaScenarioId = runCaseScenarioId(
    currentDetail,
    selectedEvaluationCaseId,
    'dca_scenario_id'
  );
  const shownDcaScenarioId = workingDcaScenarioId(
    currentConfig,
    evaluationCaseDetail,
    runDcaScenarioId
  );

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <div className={style.layout}>
      {!leftPanelCollapsed && (
        <EvalLeftPanel
          workspaces={workspaces}
          calculationsByWorkspace={calculationsCache}
          selectedCalculationId={selectedCalcId}
          onCalculationSelect={handleTileClick}
          onWorkspaceExpand={handleWorkspaceExpand}
          isAddDisabled={isCreateLoading}
          onAdd={() => setIsCreateModalOpen(true)}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          meta={workspaceMeta}
          isLoadingWorkspaces={workspacesLoadable.state === 'loading'}
          isLoadingCalculations={loadingCalcsWsId !== null}
          loadingWorkspaceId={loadingCalcsWsId}
        />
      )}

      <EvalMainContent
        isLeftPanelCollapsed={leftPanelCollapsed}
        onToggleLeftPanel={() => setLeftPanelCollapsed((prev) => !prev)}
        onRename={() => setIsRenameModalOpen(true)}
        isRenameVisible={isRenameVisible}
        onSave={() => setIsSaveModalOpen(true)}
        onCalculate={handleCalculate}
        isSaveVisible={isSaveVisible ?? false}
        isSaveDisabled={isSaveDisabled}
        isSaveLoading={isSaveLoading}
        isCalculateDisabled={isCalculateDisabled}
        calculateDisabledReason={calculateDisabledReason}
        isCalculateLoading={isCalculating}
        isAgentAvailable={!!agentMessagesHref}
        dcaScenarioId={shownDcaScenarioId}
        isZeroSum={isZeroSum}
        isDirty={isDirty}
        notifications={notifications}
        onDismissNotification={dismissNotification}
        packageId={packageId}
        evalId={selectedCalcId}
        scenarioHref={currentDetail?.scenario?.href ?? ''}
        scenarioName={currentDetail?.scenario?.name}
        resultData={resultData}
        isResultLoading={isCalculating}
        resultError={calcError ?? resultError}
        effectiveDate={workingEffectiveDate}
        setEffectiveDate={setWorkingEffectiveDate}
        aggregation={workingInterval}
        setAggregation={setWorkingInterval}
        measurementType={workingMeasurementType}
        setMeasurementType={setWorkingMeasurementType}
        slotVolumeSource={workingSlotVolumeSource}
        setSlotVolumeSource={setWorkingSlotVolumeSource}
        slotAfeSource={workingSlotAfeSource}
        setSlotAfeSource={setWorkingSlotAfeSource}
        slotVolumeConfigurations={workingSlotVolumeConfigurations}
        setSlotVolumeConfigurations={setWorkingSlotVolumeConfigurations}
        slotAfeConfigurations={workingSlotAfeConfigurations}
        setSlotAfeConfigurations={setWorkingSlotAfeConfigurations}
        slotCostDateConfigurations={workingSlotCostDateConfigurations}
        setSlotCostDateConfigurations={setWorkingSlotCostDateConfigurations}
        scheduledDates={scheduledDates}
        slotDateConfigurations={workingSlotDateConfigurations}
        setSlotDateConfigurations={setWorkingSlotDateConfigurations}
        eval2Runs={eval2Runs}
        selectedEval2Id={selectedEval2Id}
        scenarios={scenarios}
        selectedScenarioId={selectedScenarioId}
        onScenarioChange={handleScenarioChange}
        dpsList={dpsList}
        selectedDpsId={selectedDpsId}
        onDpsChange={handleDpsChange}
        drsList={
          drsLoadable.state === 'hasData' && drsLoadable.data
            ? drsLoadable.data
            : undefined
        }
        selectedDrsId={selectedDrsId}
        onDrsChange={setSelectedDrsId}
        differentialScenarioList={
          dsnLoadable.state === 'hasData' && dsnLoadable.data
            ? dsnLoadable.data
            : undefined
        }
        selectedDifferentialScenarioId={selectedDifferentialScenarioId}
        onDifferentialScenarioChange={setSelectedDifferentialScenarioId}
        taxScenarioList={
          taxScenarioLoadable.state === 'hasData' && taxScenarioLoadable.data
            ? taxScenarioLoadable.data
            : undefined
        }
        selectedTaxScenarioId={selectedTaxScenarioId}
        onTaxScenarioChange={setSelectedTaxScenarioId}
        expenseScenarioList={
          expenseScenarioLoadable.state === 'hasData' &&
          expenseScenarioLoadable.data
            ? expenseScenarioLoadable.data
            : undefined
        }
        selectedExpenseScenarioId={selectedExpenseScenarioId}
        onExpenseScenarioChange={setSelectedExpenseScenarioId}
        evalMode={evalMode}
        onEvalModeChange={setEvalMode}
        evaluationCaseList={evaluationCaseList}
        selectedEvaluationCaseId={selectedEvaluationCaseId}
        onEvaluationCaseChange={setSelectedEvaluationCaseId}
        evaluationCaseDetail={evaluationCaseDetail}
        dpsSlotCount={dpsScheduleResult?.slotCount ?? null}
        dpsScheduleStatus={dpsScheduleStatus}
        snapshotStatusItems={snapshotProgress.statusItems}
        snapshotOverallStatus={snapshotProgress.overallStatus}
        calculationId={currentDetail?.calculationId ?? undefined}
        evaluationCalculationId={currentDetail?.id}
        runDcaScenarioId={runDcaScenarioId}
        evalExpiration={evalExpiration}
        evalDate={evalDate}
        isPageLoading={isPageLoading}
        partiallyCoveredWellsHref={partiallyCoveredWellsHref}
        partiallyCoveredWellsCount={partiallyCoveredWellsCount}
        calculationWarnings={calculationWarnings}
        onExportAries={handleExportAries}
        isExportAriesDisabled={isExportAriesDisabled}
        isExportAriesLoading={isExportAriesLoading}
        exportAriesTooltip={exportAriesTooltip}
        onExportAriesMdb={handleOpenAriesMdbModal}
      />

      {agentMessagesHref && agentWorkspace && (
        <AgentSurface
          agent={EVAL_AGENT}
          agentMessagesHref={agentMessagesHref}
          subject={{ type: 'EvaluationWorkspace', id: agentWorkspace.id }}
          subtitle={`Ask about ${agentWorkspace.name}`}
          model={agentWorkspace.agentMessages?.model}
          onToolResult={handleAgentToolResult}
          pendingState={reportPendingState}
        />
      )}

      {unsavedChanges.showModal && (
        <UnsavedChangesModal
          message="You have unsaved changes in your current work session. If you leave this page, those changes will be lost."
          onKeep={() => unsavedChanges.setShowModal(false)}
          onDiscard={() => unsavedChanges.confirmDiscard()}
        />
      )}

      <SaveEvaluationModal
        isOpen={isSaveModalOpen}
        isLoading={isSaveLoading}
        onClose={() => setIsSaveModalOpen(false)}
        onSubmit={handleSave}
      />

      <CreateWorkspaceModal
        isOpen={isCreateModalOpen}
        isLoading={isCreateLoading}
        onClose={() => setIsCreateModalOpen(false)}
        onSubmit={handleCreateWorkspace}
        scenarios={
          scenarios?.map((s) => ({
            id: s.id,
            label: `${s.name}${s.interestType ? ` - ${s.interestType}` : ''}`,
          })) ?? []
        }
        dpsList={
          dpsList?.map((d) => ({
            id: d.id,
            label: `${d.name} | ${d.paceModel}`,
          })) ?? []
        }
        evaluationCaseList={evaluationCaseList}
        isEvalPreselected={isZeroSum}
      />

      <RenameWorkspaceModal
        isOpen={isRenameModalOpen}
        isLoading={isRenameLoading}
        onClose={() => setIsRenameModalOpen(false)}
        onSubmit={handleRename}
        currentName={
          allWorkspaces.find((ws) => ws.id === activeWorkspaceId)?.name ?? ''
        }
      />

      <AriesMdbExportModal
        isOpen={isAriesMdbModalOpen}
        isLoading={ariesMdbExport.isSubmitting}
        isLoadingDefaults={ariesMdbExport.isLoadingDefaults}
        defaults={ariesMdbExport.defaults}
        errorMessage={ariesMdbExport.error}
        runStarted={ariesMdbRunStarted}
        restoredOutputName={ariesMdbRestore?.config?.outputName ?? null}
        statusItems={ariesMdbProgress.statusItems}
        overallStatus={ariesMdbProgress.overallStatus}
        bundle={ariesMdbProgress.bundle}
        onClose={handleCloseAriesMdbModal}
        onSubmit={handleSubmitAriesMdb}
        onRunInBackground={handleRunInBackgroundAriesMdb}
      />
    </div>
  );
};
